import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../redux/store'
import PiggyBankAnimation from './piggy-bank.json'
import { Player } from '@lottiefiles/react-lottie-player'
import NumberFlow from '@number-flow/react'
import { fetchJackpots } from '../../redux/slices/jackpotSlice'
import { useAppDispatch } from '../../redux/hooks'
import { AnimationItem } from 'lottie-web'
import { CoreUtils } from '@smartico/public-api'
import Draggable from 'react-draggable'
import { Smartico } from '../../core/Smartico'
import { useOutsideClick } from '../../hooks/useOutsideClick'

type Position = {
	x: number
	y: number
}

const getDragArea = () => {
	const height = Math.min(window.innerHeight, window.visualViewport?.height ?? window.innerHeight)
	const header = document.querySelector('.header')
	const bottomMenu = document.querySelector('.bottom-menu')
	return {
		width: window.innerWidth,
		top: header ? Math.max(0, header.getBoundingClientRect().bottom) : 0,
		bottom: bottomMenu ? Math.min(height, bottomMenu.getBoundingClientRect().top) : height,
	}
}

const PiggyBank = () => {
	const dispatch = useAppDispatch()
	const jackpots = useSelector((state: RootState) => state.jackpot.jackpots)
	const jackpot = jackpots.find((jackpot) => jackpot.jp_template_id === 8)

	const [jackpotAmount, setJackpotAmount] = useState(0)
	const [jackpotCurrency, setJackpotCurrency] = useState('')
	const [prevJackpotAmount, setPrevJackpotAmount] = useState(0)
	const [contributeAnimation, setContributeAnimation] = useState<AnimationItem | null>(null)
	const [lastChangeTime, setLastChangeTime] = useState<number>(Date.now())
	const [position, setPosition] = useState<Position>({ x: 0, y: 0 })
	const [showTooltip, setShowTooltip] = useState(false)

	const piggyRef = useRef<HTMLDivElement | null>(null)
	const tooltipRef = useRef<HTMLDivElement | null>(null)
	const [piggySize, setPiggySize] = useState({ width: 0, height: 0, overflowBottom: 0 })

	const [area, setArea] = useState(getDragArea)
	
	useOutsideClick(tooltipRef, () => setShowTooltip(false))

	useEffect(() => {
		const savedPosition = localStorage.getItem('piggyBankPosition')
		if (savedPosition) {
			try {
				setPosition(JSON.parse(savedPosition))
				return
			} catch {}
		}
		setPosition({ x: Number.MAX_SAFE_INTEGER, y: Number.MAX_SAFE_INTEGER })
	}, [])

	const measure = () => {
		setArea(getDragArea())
		if (piggyRef.current) {
			const rect = piggyRef.current.getBoundingClientRect()
			const contentBottom = Array.from(piggyRef.current.children).reduce((max, child) => Math.max(max, child.getBoundingClientRect().bottom), rect.bottom)
			setPiggySize({
				width: rect.width,
				height: rect.height,
				overflowBottom: contentBottom - rect.bottom,
			})
		}
	}

	useEffect(() => {
		const handleResize = () => measure()
		handleResize()
		const timeoutId = setTimeout(handleResize, 1000)
		window.addEventListener('resize', handleResize)
		window.visualViewport?.addEventListener('resize', handleResize)
		return () => {
			clearTimeout(timeoutId)
			window.removeEventListener('resize', handleResize)
			window.visualViewport?.removeEventListener('resize', handleResize)
		}
	}, [])

	const bounds = {
		left: 0,
		top: area.top,
		right: Math.max(0, area.width - piggySize.width),
		bottom: Math.max(area.top, area.bottom - piggySize.height - piggySize.overflowBottom),
	}

	useEffect(() => {
		if (!piggySize.height) return

		const newX = Math.min(Math.max(position.x, bounds.left), bounds.right)
		const newY = Math.min(Math.max(position.y, bounds.top), bounds.bottom)

		if (newX !== position.x || newY !== position.y) {
			setPosition({ x: newX, y: newY })
			localStorage.setItem('piggyBankPosition', JSON.stringify({ x: newX, y: newY }))
		}
	}, [bounds.left, bounds.top, bounds.right, bounds.bottom, piggySize.height, position])

	useEffect(() => {
		const intervalId = setInterval(() => {
			if (Date.now() - lastChangeTime >= 30000) {
				contributeAnimation?.playSegments([35, 66], true)
				setLastChangeTime(Date.now())
			}
		}, 1000)

		return () => clearInterval(intervalId)
	}, [lastChangeTime, contributeAnimation])

	useEffect(() => {
		dispatch(fetchJackpots())
		const interval = setInterval(() => dispatch(fetchJackpots()), 1000)
		return () => clearInterval(interval)
	}, [dispatch])

	useEffect(() => {
		if (jackpot) {
			setJackpotAmount(jackpot.pot.current_pot_amount)
			setJackpotCurrency(jackpot.user_currency)
		}
	}, [jackpot, jackpotAmount])

	useEffect(() => {
		if (jackpotAmount !== prevJackpotAmount) {
			contributeAnimation?.playSegments([0, 35], true)
			setPrevJackpotAmount(jackpotAmount)
			setLastChangeTime(Date.now())
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [jackpotAmount, prevJackpotAmount])

	const handlePiggyBankClick = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation()
		Smartico.lib().action('piggy-bank-click')
	}

	return (
		<Draggable
			bounds={bounds}
			position={position}
			onStart={measure}
			onStop={(e, data) => {
				setPosition({ x: data.x, y: data.y })
				localStorage.setItem('piggyBankPosition', JSON.stringify({ x: data.x, y: data.y }))
			}}
		>
			<div
				className="piggy-bank"
				onClick={handlePiggyBankClick}
				ref={piggyRef}
				style={{
					position: 'fixed',
					left: 0,
					top: 0,
				}}
			>
				<Player lottieRef={setContributeAnimation} autoplay={false} loop={false} src={PiggyBankAnimation} style={{ height: '100%', width: '100%' }} />
				<div className="piggy-bank-handle" />
				<div className="jackpot-pot">
					<div className="jackpot-currency">{CoreUtils.currencySymbol(jackpotCurrency)}</div>
					<NumberFlow className="pot-value" format={{ useGrouping: false }} value={jackpotAmount ? Number(jackpotAmount.toFixed(2)) : 0.0} />
					<div
						className="tooltip"
						onClick={(e) => {
							e.stopPropagation()
							setShowTooltip((prev) => !prev)
						}}
						onTouchStart={(e) => {
							e.stopPropagation()
							setShowTooltip((prev) => !prev)
						}}
					>
						i
						{showTooltip && (
							<div className="tooltip-text" ref={tooltipRef}>
								This is the Piggy Bank. You can contribute to the Jackpot by clicking on it. The Jackpot is shared between all players who have contributed to it. The more you
								contribute, the bigger your share of the Jackpot will be. Good luck!
							</div>
						)}
					</div>
				</div>
			</div>
		</Draggable>
	)
}

export default PiggyBank
